import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';

const milestones = [
  { year: '2015', title: '平台创立', desc: '在北京正式成立，专注成人学历教育领域，首年服务学员超过5000人。' },
  { year: '2017', title: '课程升级', desc: '全面升级为高清视频课程体系，引入名师直播辅导，学员满意度达到96%。' },
  { year: '2019', title: '规模突破', desc: '合作高校突破100所，累计服务学员突破20万人，覆盖全国31个省市。' },
  { year: '2021', title: '技术革新', desc: '推出AI智能学习系统，个性化学习路径规划，学习效率提升40%。' },
  { year: '2024', title: '行业领先', desc: '累计服务学员超过46万人，成为成人学历教育领域标杆品牌。' },
  { year: '2026', title: '持续创新', desc: '持续优化课程品质与服务体验，致力于让每一位在职人士都能轻松提升学历。' },
];

const honors = [
  { icon: 'ri-award-line', title: '教育部备案正规机构', desc: '持有教育部门颁发的办学许可证，所有合作高校均为教育部备案的正规高等院校。' },
  { icon: 'ri-shield-check-line', title: '学信网认证保障', desc: '所有学历证书均可在学信网（中国高等教育学生信息网）终身查询验证。' },
  { icon: 'ri-star-line', title: '98.6%学员满意度', desc: '基于超过10万条真实评价统计，教学质量和服务水平获得学员广泛认可。' },
  { icon: 'ri-building-2-line', title: '128所合作高校', desc: '与全国128所高校建立官方合作关系，提供丰富的专业和院校选择。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    const formData = new FormData(form);
    if (honeypotEl) formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q7ljue3oq4jqumthk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 成人高清学历提升平台"
        description="成人高清学历提升平台品牌介绍——专注成人学历教育多年，累计服务46万+学员，合作128所高校。了解我们的品牌故事、信誉资质与联系方式。"
        keywords="关于我们,成人高清,学历提升平台,品牌介绍,信誉资质,联系我们"
        canonicalUrl="https://www.chengrengaoqing.com/about"
        ogTitle="关于我们 - 成人高清学历提升平台"
        ogDescription="了解成人高清学历提升平台的品牌故事、信誉资质与联系方式。专注成人学历教育，服务46万+学员。"
      />
      <SchemaOrg type="Organization" data={{}} />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 成人高清学历提升平台',
          description: '了解成人高清学历提升平台的品牌故事、信誉资质与联系方式。',
          url: 'https://www.chengrengaoqing.com/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
                  品牌介绍
                </h1>
                <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                  成人高清学历提升平台成立于2015年，是国内领先的成人学历教育服务平台。
                  我们致力于为在职人士提供高清视频课程、名师直播辅导与灵活学习方案，
                  帮助每一位学员轻松获得国家认可学历，实现职场晋升与个人成长。
                </p>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-accent-200 -translate-x-1/2 hidden md:block"></div>
                <div className="flex flex-col gap-8">
                  {milestones.map((item, index) => (
                    <div
                      key={index}
                      className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                          <div className="text-sm text-accent-600 font-medium mb-1">{item.year}</div>
                          <h3 className="font-heading text-lg text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-accent-500 text-background-50 shrink-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <div className="flex-1 hidden md:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Honors & Reputation */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                  信誉资质
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                  正规资质，值得信赖。我们始终坚持规范办学、诚信服务。
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {honors.map((item, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-xl border border-background-200/70 p-6 text-center"
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-50 mx-auto mb-4">
                      <i className={`${item.icon} text-2xl text-accent-600`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                  有任何关于学历提升的问题？请随时联系我们，专业顾问为您解答。
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-10">
                <div className="lg:w-2/5">
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 shrink-0">
                        <i className="ri-phone-line text-lg text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-900 mb-1">咨询热线</h3>
                        <a href="tel:4008889999" className="text-base text-primary-600 font-medium cursor-pointer">400-888-9999</a>
                        <p className="text-xs text-foreground-400 mt-0.5">工作日 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 shrink-0">
                        <i className="ri-mail-line text-lg text-accent-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                        <span className="text-base text-foreground-700">service@chengrengaoqing.com</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 shrink-0">
                        <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
                        <span className="text-sm text-foreground-600 leading-relaxed">
                          北京市海淀区中关村大街1号创新大厦12层
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 shrink-0">
                        <i className="ri-wechat-line text-lg text-foreground-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-900 mb-1">官方微信</h3>
                        <span className="text-sm text-foreground-600">chengrengaoqing</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-3/5">
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8"
                  >
                    <h3 className="font-heading text-lg text-foreground-900 mb-5">在线咨询</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm text-foreground-700 mb-1.5">姓名 *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-foreground-700 mb-1.5">手机号码 *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的手机号码"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm text-foreground-700 mb-1.5">电子邮箱</label>
                      <input
                        type="email"
                        name="email"
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm text-foreground-700 mb-1.5">意向学历提升方式</label>
                      <select
                        name="category"
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                      >
                        <option value="">请选择</option>
                        <option value="成人高考">成人高考</option>
                        <option value="自学考试">自学考试</option>
                        <option value="网络教育">网络教育</option>
                        <option value="国家开放大学">国家开放大学</option>
                        <option value="未确定">尚未确定</option>
                      </select>
                    </div>

                    <div className="mb-5">
                      <label className="block text-sm text-foreground-700 mb-1.5">咨询内容</label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请简要描述您想了解的内容（限500字）"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      className="form-honeypot"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交咨询
                    </button>

                    {formStatus === 'success' && (
                      <p className="mt-4 text-sm text-accent-600">感谢您的咨询！我们的顾问将在1个工作日内与您联系。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="mt-4 text-sm text-red-500">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}